export function observe(target: string, callback: (el: Element, added: boolean) => void) {
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "childList") {
        // Check added nodes
        for (const node of mutation.addedNodes) {
          const element = node as Element;
          if (element.matches && element.matches(target) && element.getAttribute("data-observed") !== "true") {
            element.setAttribute("data-observed", "true");
            callback(element, true);
          }

          const childMatches = element.querySelectorAll ? element.querySelectorAll(target) : [];
          for (const match of childMatches) {
            if (match.getAttribute("data-observed") !== "true") {
              match.setAttribute("data-observed", "true");
              callback(match, true);
            }
          }
        }
        // Check removed nodes
        for (const node of mutation.removedNodes) {
          const element = node as Element;
          if (element.matches && element.matches(target) && element.getAttribute("data-observed-remove") !== "true") {
            element.setAttribute("data-observed-remove", "true");
            callback(element, false);
          }

          const childMatches = element.querySelectorAll ? element.querySelectorAll(target) : [];
          for (const match of childMatches) {
            if (match.getAttribute("data-observed-remove") !== "true") {
              match.setAttribute("data-observed-remove", "true");
              callback(match, false);
            }
          }
        }
      }
    }
  });

  const observer = () =>
    mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", observer);
  } else {
    observer();
  }

  return mutationObserver;
}

const scriptCallbacks: Record<string, Array<() => void>> = {};
const addScriptCallback = (src: string, callback: () => void) => {
  if (!scriptCallbacks[src]) {
    scriptCallbacks[src] = [];
  }
  scriptCallbacks[src].push(callback);
};

export function loadScript(src: string, callback: () => void) {
  const existingScript = document.querySelector(`script[src="${src}"]`);
  if (existingScript) {
    if (existingScript.getAttribute("data-loaded") === "true") {
      callback();
    } else {
      addScriptCallback(src, callback);
    }
    return;
  }

  addScriptCallback(src, callback);

  const scriptTag = document.createElement("script");
  scriptTag.src = src;
  scriptTag.onload = () => {
    scriptTag.setAttribute("data-loaded", "true");
    const cbs = scriptCallbacks[src] || [];
    cbs.forEach((cb) => cb());
    scriptCallbacks[src] = [];
  };
  document.body.appendChild(scriptTag);
}
